import { Injectable } from '@angular/core';
import { StateService } from '@uirouter/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { NOTIFICATION_CONSTANT } from '@client/app/modules/notifications/constant/notification.constant';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { Domain } from '@monsido/modules/models/api/domain';
import { PageDetailsComponent } from '@client/app/components/pagedetails/details/page-details.component';
import { Page } from '@monsido/modules/page-details/models';
import { CsvExportsOverlayComponent } from '@client/app/modules/export/components/overlay/csv-exports.component';

@Injectable({ providedIn: 'root' })
export class NotificationsRedirectService {
    private domains: Domain[] = [];

    constructor (
        private readonly stateService: StateService,
        private readonly monEventService: MonEventService,
        private readonly totalDomainsService: TotalDomainsService,
        private readonly sessionService: SessionService,
        private readonly pageService: PageService,
        private readonly monUIRouterTransitionService: MonUIRouterTransitionService,
    ) {
        if (this.totalDomainsService.domainsEssential) {
            this.totalDomainsService.domainsEssential.subscribe((updatedDomains: Domain[] | null) => {
                if (updatedDomains) {
                    this.domains = updatedDomains;
                }
            });
        }
    }

    private getScopeId (scopes: { id: number; type: string; }[], type: string): number | null {
        const scopeData = scopes.find(scope => scope.type.toLowerCase() === type.toLowerCase());
        return scopeData?.id ?? null;
    }

    private getAvailableDomainId (): number | null {
        const currentDomainId = this.sessionService.getDomainId();
        if (currentDomainId) {
            return currentDomainId;
        }
        return Array.isArray(this.domains) ? this.domains.find(domain => domain.active)?.id ?? null : null;
    }

    private async openPage (id: number): Promise<void> {
        try {
            const page: Page = await this.pageService.get(id) as Page;
            const params = { body: PageDetailsComponent, size: 'lg', classes: 'page-details-app', data: { page } };
            this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
        } catch { }
    }

    private navigateTo (stateName: string, extraParams: Record<string, unknown> = {}): void {
        const customerId = this.sessionService.getCustomerId();
        this.monUIRouterTransitionService.onTransitionFinished(() =>
            this.stateService.go(
                stateName,
                { customerId, ...extraParams },
                { reload: 'base.customer.domain', inherit: false },
            ).then(() => {}));
    }

    private redirectToPage (domainId: number, targetId: number | undefined): void {
        const params = this.stateService.params;
        const rawDomainId = params['domainId'] as string | undefined;
        const isSameDomain = rawDomainId && Number.parseInt(rawDomainId, 10) === domainId;
        if (isSameDomain && targetId !== undefined) {
            this.openPage(targetId);
        } else if (!isSameDomain) {
            this.navigateTo('base.customer.domain.dashboard', { domainId, page_id: targetId });
        }
    }

    private resolveSpellingState (target: string): string {
        return target === NOTIFICATION_CONSTANT.TARGET.SPELLING_DICTIONARY
            ? 'base.customer.domain.qa.spellcheck.dictionary'
            : 'base.customer.domain.qa.spellcheck.pages';
    }

    private resolveAccessibilityState (action: string): string {
        const isReviewedOrIgnored = [
            NOTIFICATION_CONSTANT.ACTION.ACCESSIBILITY_CHECK_REVIEWED,
            NOTIFICATION_CONSTANT.ACTION.ACCESSIBILITY_CHECK_IGNORED,
        ].includes(action);
        return isReviewedOrIgnored
            ? 'base.customer.domain.accessibility.ignored_pages'
            : 'base.customer.domain.accessibility.pages';
    }

    private resolveSeoState (target: string): string {
        return target === NOTIFICATION_CONSTANT.TARGET.SEO_ISSUE
            ? 'base.customer.domain.seo.issues'
            : 'base.customer.domain.seo.pages';
    }

    private redirectToDomain (action: string, targetId: number | undefined): void {
        const state = action === NOTIFICATION_CONSTANT.ACTION.COOKIES_FOUND
            ? 'base.customer.domain.data_privacy.consent_overview.cookie_overview'
            : 'base.customer.domain.dashboard';
        this.navigateTo(state, { domainId: targetId });
    }

    redirect (args: { scopes: { id: number; type: string; }[]; action: string; target: string; target_id?: number }): void {
        let domainId = this.getScopeId(args.scopes, 'domain');
        const { action, target, target_id } = args;

        switch (target) {
            case NOTIFICATION_CONSTANT.TARGET.PAGE:
                if (domainId !== null) {
                    this.redirectToPage(domainId, target_id);
                }
                break;
            case NOTIFICATION_CONSTANT.TARGET.LINK:
                if (domainId !== null) {
                    this.navigateTo('base.customer.domain.qa.broken.links', { domainId });
                }
                break;
            case NOTIFICATION_CONSTANT.TARGET.SPELLING_DICTIONARY:
            case NOTIFICATION_CONSTANT.TARGET.SPELLING_CONFIRMED:
            case NOTIFICATION_CONSTANT.TARGET.SPELLING_IGNORED:
                domainId = this.getAvailableDomainId();
                if (domainId) {
                    this.navigateTo(this.resolveSpellingState(target), { domainId });
                }
                break;
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_ERRORS_PAGE:
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_SOURCE_CODE:
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_CHECK:
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_ERROR:
                if (domainId !== null) {
                    this.navigateTo(this.resolveAccessibilityState(action), { domainId });
                }
                break;
            case NOTIFICATION_CONSTANT.TARGET.SEO_ISSUE:
            case NOTIFICATION_CONSTANT.TARGET.SEO_PAGE_ISSUE:
                if (domainId !== null) {
                    this.navigateTo(this.resolveSeoState(target), { domainId });
                }
                break;
            case NOTIFICATION_CONSTANT.TARGET.REQUEST_SCAN:
                if (domainId !== null && target_id !== undefined) {
                    this.navigateTo('base.customer.domain.dashboard', { domainId, page_id: target_id });
                }
                break;
            case NOTIFICATION_CONSTANT.TARGET.EXPORT:
                this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params: { body: CsvExportsOverlayComponent } });
                break;
            case NOTIFICATION_CONSTANT.TARGET.DOMAIN:
                this.redirectToDomain(action, target_id);
                break;
        }
    }
}
